/* eslint-disable no-unreachable */
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { get, pick, map, keys } from "lodash";
import { Formik } from "formik";
import { TextField } from "@mui/material";
import Dropzone from "react-dropzone";
import { v4 as uuidv4 } from "uuid";

import {
  createCategoryApi,
  getUrlForUploadApi,
  updateCategoryApi,
} from "../../apis/admin";
import { getCategoryByIdApi } from "../../apis/portal";
import { rawAxiosApi } from "../../apis/raw";
import { CreateCategorySchema } from "../../globals/validations";
import {
  POONAWALLA_DIALOG_TYPES,
  POONAWALLA_USER_ROLES_TYPES,
  PAGE_MODES,
  ROUTE_DASHBOARD,
  ROUTE_SIGNIN,
} from "../../globals/constants";
import { showErrorToaster, showSuccessToaster } from "../../globals/toaster";
import { RefreshDataActions } from "../../redux/reducers/RefreshDataReducer";
import { verifyToken } from "../../globals/utils/jwtUtils";

import PoonaWallaModal from "../PoonaWallaModal";
import PoonaWallaBasicLoader from "../PoonaWallaBasicLoader";

const CategoryModal = () => {
  const { authData } = useSelector(
    (store) => ({
      authData: store.auth.data || {},
    }),
    shallowEqual
  );

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const qp = new URLSearchParams(location.search);

  const initialFormValues = {
    name: "",
    description: "",
    banner_url: "",
    status: "ACTIVE",
  };

  const [pageMode, setPageMode] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [categoryData, setCategoryData] = useState(initialFormValues);

  const handleUnmount = () => {
    // Remove Application Qparam & replace
    qp.delete("categorymodal");
    history.replace({
      search: qp.toString(),
    });
  };

  const handleCategoryConfig = async (
    values,
    { setSubmitting, setFieldError, setStatus }
  ) => {
    try {
      setSubmitting(true);
      if (values.banner_url.startsWith("blob:") && values.file) {
        const {
          data: { putUrl, getUrl },
        } = await getUrlForUploadApi({
          key: `${uuidv4()}.${values.file.name.split(".")[1]}`,
        });
        await rawAxiosApi(putUrl, "PUT", values.file, {
          "Content-Type": get(values, "file.type", "image/svg+xml"),
        });
        values.banner_url = getUrl;
        delete values.file;
      }
      if (pageMode === PAGE_MODES.CREATE) {
        await createCategoryApi(values);
        showSuccessToaster("Category Created.");
      } else {
        await updateCategoryApi(qp.get("categorymodal"), values);
        showSuccessToaster("Category Updated.");
      }
      handleUnmount();
      dispatch(RefreshDataActions.SET_TO_REFRESH());
    } catch (error) {
      const data = get(error, "data");
      if (get(data, "type") === POONAWALLA_DIALOG_TYPES.FIELD) {
        map(keys(get(data, "detail")), (key) =>
          setFieldError(key, get(data, ["detail", key], ""))
        );
      } else {
        showErrorToaster(get(error, "data.detail", "Something went wrong."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetCategory = async (id) => {
    try {
      setIsLoading(true);
      const { data } = await getCategoryByIdApi(id);
      setCategoryData(
        pick(data, ["name", "description", "banner_url", "status"])
      );
    } catch (error) {
      showErrorToaster(get(error, "data.detail", "Something went wrong."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (qp.get("categorymodal")) {
      // IF NOT LOGGED IN
      if (!get(authData, "idToken") || !verifyToken(get(authData, "idToken"))) {
        history.push(ROUTE_SIGNIN);
      } else if (
        // IF LOGGED IN BUT NOT AN ADMIN, THEN THROW HIM INTO DASHBOARD
        ![
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
        ].includes(get(authData, "user_type"))
      ) {
        history.push(ROUTE_DASHBOARD);
      } else {
        // CREATE APPLICATION MODE
        if (qp.get("categorymodal") === "new") {
          setPageMode(PAGE_MODES.CREATE);
        } else {
          // UPDATE APPLICATION MODE
          setPageMode(PAGE_MODES.EDIT);
          handleGetCategory(qp.get("categorymodal"));
        }
      }
    } else {
      setPageMode(undefined);
      setCategoryData(initialFormValues);
    }
  }, [location.search]);

  return (
    <>
      {pageMode && (
        <PoonaWallaModal
          size="md"
          handleHideModal={handleUnmount}
          isActive={Boolean(pageMode)}
          title={
            pageMode === PAGE_MODES.CREATE
              ? "Create Category"
              : "Update Category"
          }
        >
          {isLoading ? (
            <PoonaWallaBasicLoader isLoading={isLoading} />
          ) : (
            <div className="CreateCategoryModal">
              <Formik
                initialValues={categoryData}
                validationSchema={CreateCategorySchema()}
                onSubmit={handleCategoryConfig}
              >
                {({
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <>
                    <form onSubmit={handleSubmit}>
                      <TextField
                        name="name"
                        label="Category Name"
                        error={errors.name && touched.name}
                        helperText={
                          errors.name && touched.name ? errors.name : ""
                        }
                        defaultValue={values.name}
                        value={values.name}
                        onChange={(e) => setFieldValue("name", e.target.value)}
                        onBlur={handleBlur}
                        variant="standard"
                        className="w-100"
                      />
                      <TextField
                        name="description"
                        label="Description"
                        multiline
                        error={errors.description && touched.description}
                        helperText={
                          errors.description && touched.description
                            ? errors.description
                            : ""
                        }
                        defaultValue={values.description}
                        value={values.description}
                        onChange={(e) =>
                          setFieldValue("description", e.target.value)
                        }
                        onBlur={handleBlur}
                        variant="standard"
                        className="w-100 mt-3"
                      />
                      <div className="mt-5">
                        <Dropzone
                          onDrop={(acceptedFiles) => {
                            const dropzoneElm =
                              document.getElementById("PoonaWallaDropzone");
                            if (
                              dropzoneElm.classList.contains(
                                "PoonaWallaDropzone-enter"
                              )
                            ) {
                              dropzoneElm.classList.remove(
                                "PoonaWallaDropzone-enter"
                              );
                            }
                            const [file] = acceptedFiles;
                            if (file) {
                              values.banner_url = URL.createObjectURL(file);
                              values.file = file;
                            }
                          }}
                          multiple={false}
                          accept="image/*"
                          onDragEnter={(e) =>
                            document
                              .getElementById("PoonaWallaDropzone")
                              ?.classList.add("PoonaWallaDropzone-enter")
                          }
                          onDragLeave={(e) => {
                            const dropzoneElm =
                              document.getElementById("PoonaWallaDropzone");
                            if (
                              dropzoneElm.classList.contains(
                                "PoonaWallaDropzone-enter"
                              )
                            ) {
                              dropzoneElm.classList.remove(
                                "PoonaWallaDropzone-enter"
                              );
                            }
                          }}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div
                              className={`PoonaWallaDropzone ${
                                (!values.file || !values.banner_url) &&
                                errors.banner_url &&
                                "border-danger"
                              }`}
                              id="PoonaWallaDropzone"
                            >
                              <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                {values.banner_url ? (
                                  <div className="PoonaWallaDropzone__preview d-flex justify-content-center align-items-center p-3">
                                    <img
                                      src={values.banner_url}
                                      className="PoonaWallaDropzone__preview-img"
                                      alt={"Category Banner Preview"}
                                      id="PoonaWallaDropzone-preview"
                                    />
                                  </div>
                                ) : (
                                  <div className="PoonaWallaDropzone__container p-5 d-flex flex-column justify-content-center align-items-center">
                                    <span className="PoonaWallaDropzone__container-logo">
                                      <i
                                        className="fa fa-upload"
                                        aria-hidden="true"
                                      ></i>
                                    </span>
                                    <span className="PoonaWallaDropzone__container-title mt-1 ">
                                      Upload Logo
                                    </span>
                                    <span className="PoonaWallaDropzone__container-description">
                                      Drag file here or Click to select
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Dropzone>
                        {(!values.file || !values.banner_url) &&
                          errors.banner_url && (
                            <span className="text-danger fs-0-8">
                              {get(errors, "banner_url")}
                            </span>
                          )}
                      </div>
                      <button
                        type="submit"
                        className={`MainButton MainButton__primary outlined mt-5 ${
                          isSubmitting && "loading"
                        }`}
                        disabled={isSubmitting}
                      >
                        {pageMode === PAGE_MODES.CREATE ? "Create" : "Submit"}
                      </button>
                    </form>
                  </>
                )}
              </Formik>
            </div>
          )}
        </PoonaWallaModal>
      )}
    </>
  );
};

export default CategoryModal;
